import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { INavbarIconVariant, NavbarIcon } from "../../atoms/navbar/Icon";
import { useQuestionnaireAnswers } from "../../hooks/useAnswers";
import { CustomAnswer, Question, QuestionLogicThen, Questionnaire } from "../../models/types";
import {
  addQuestionnaireAnswer,
  editQuestionnaireAnswer,
} from "../../services/QuestionnaireAnswerService";
import queryClient, { QueryKey } from "../../state/QueryStore";
import { QuestionIconIndex } from "../template-builder/QuestionIconIndex";
import { QuestionDisplay } from "./QuestionDisplay";
import { QuestionNavigation } from "./QuestionNavigation";

type WizardQuestionDisplayProps = {
  question: Question;
  questionnaire: Questionnaire;
  questionIndex: number;
  answer: CustomAnswer;
  onChange: (value: string | null, models?: string) => void;
  relatedObjectId: string;
  handleNext: () => void;
  valueError: boolean;
  loading: boolean;
  handlePrevious: () => void;
  relatedObjectName: string;
  setLoading?: (value: boolean) => void;
  paddingTop?: string;
  preview?: boolean;
  sessionID?: string | null;
  handleLogic?: () => QuestionLogicThen | null;
  questionGroup?: string;
  allQuestions?: Question[];
};

export const WizardQuestionDisplay = ({
  question,
  questionnaire,
  questionIndex,
  answer,
  onChange,
  relatedObjectId,
  handleNext,
  valueError,
  loading,
  handlePrevious,
  relatedObjectName,
  setLoading,
  paddingTop = "0px",
  preview = false,
  sessionID,
  handleLogic,
  questionGroup,
  allQuestions,
}: WizardQuestionDisplayProps) => {
  const theme = useTheme();
  const { data: oldAnswer } = useQuestionnaireAnswers({
    questionnaire: questionnaire.id,
    question: question?.ref,
    ...(relatedObjectId !== "" && { "related_object_id[]": [relatedObjectId] }),
    ...(sessionID && { public_session: sessionID }),
  });
  const [fieldRequiredError, setFieldRequiredError] = useState<boolean>(false);
  const questionFieldRef = useRef();
  const handleSaveDraft = async (models?: string[]) => {
    if (preview) {
      handleNext();
      return;
    }
    setLoading && setLoading(true);
    try {
      // Save draft of the question
      if (questionFieldRef.current) {
        const isValid = (questionFieldRef?.current as any)?.validate();
        if (!isValid) {
          return;
        }
      }

      // Check if it's required and not empty
      if (
        question.required &&
        (!answer.answer ||
          (answer.answer && answer.answer === "") ||
          (answer.answer && Array.isArray(answer.answer) && answer.answer.length === 0) ||
          (answer.answer && answer.answer === "null") ||
          (answer.answer && answer.answer === "[]"))
      ) {
        return setFieldRequiredError(true);
      } else {
        setFieldRequiredError(false);
      }

      let lastAnswer: boolean = false;
      if (handleLogic) {
        const outcome: QuestionLogicThen | null = handleLogic();
        if (outcome) {
          if (outcome.action === "finish") {
            lastAnswer = true;
          }
        }
      }
      // Only include the flag when this really is the last answer
      const extraParams = lastAnswer ? { last_answer: "true" } : {};

      if (oldAnswer && oldAnswer?.results.length > 0) {
        await editQuestionnaireAnswer(oldAnswer?.results[0].id, {
          answer: answer.answer,
          status: "PENDING REVIEW",
          public_session: sessionID,
        });
      } else {
        await addQuestionnaireAnswer(
          {
            ...answer,
            status: "PENDING REVIEW",
            public_session: sessionID,
          },
          extraParams
        );
      }
      queryClient.invalidateQueries(QueryKey.UseCase);
      handleNext();
    } catch (e) {
      setLoading && setLoading(false);
    }
  };

  useEffect(() => {
    if (oldAnswer && oldAnswer?.results.length > 0 && !preview) {
      onChange(oldAnswer?.results[0].answer);
    }
  }, [oldAnswer]);

  useEffect(() => {
    setFieldRequiredError(false);
  }, [question]);

  return (
    <Box
      display="flex"
      flexGrow={1}
      paddingTop={paddingTop}
      width="100%"
      flexDirection="column"
      gap="16px"
    >
      <Box display="flex" gap="8px" alignItems="center" width="100%">
        {questionGroup && (
          <Box display="flex" gap="8px" alignItems="center">
            <QuestionIconIndex
              icon="audit-log"
              bgcolor={theme.palette.custom.primaryBorder}
              color={theme.palette.custom.primaryTypography}
              index={questionGroup}
              width="unset"
            />
            <NavbarIcon
              variant="forward-slash"
              sx={{ width: "12px", height: "12px", minHeight: "12px", minWidth: "12px" }}
            />
          </Box>
        )}
        <QuestionIconIndex
          icon={question?.icon as INavbarIconVariant}
          bgcolor={theme.palette.custom.primaryBorder}
          color={theme.palette.custom.primaryTypography}
          index={(questionIndex + 1).toString()}
          fill={
            question?.icon === "reports-list" ||
            question?.icon === "single-choice" ||
            question?.icon === "yes-no" ||
            question?.icon === "dropdown"
              ? theme.palette.custom.primaryTypography
              : undefined
          }
          width="unset"
        />
      </Box>
      <QuestionDisplay
        ref={questionFieldRef}
        hideQuestionNumber
        hideQuestionnaireName
        question={question}
        questionnaire={questionnaire}
        questionIndex={questionIndex}
        value={answer?.answer}
        onChange={onChange}
        valueError={valueError}
        relatedObjectId={relatedObjectId}
        handleNext={() => handleSaveDraft()}
        relatedObjectName={relatedObjectName}
        sessionID={sessionID}
      />
      {fieldRequiredError && (
        <Typography variant="body2" color="error">
          This field is required
        </Typography>
      )}
      <QuestionNavigation
        isQuestionnaireFinalized={false}
        impactAssessmentId={""}
        loading={loading}
        questionnaireId={""}
        currentQuestionIndex={questionIndex}
        handleNext={() => handleSaveDraft()}
        handlePrevious={handlePrevious}
        isSingleQuestion={false}
        question={question}
        questionsCount={allQuestions?.length ?? questionnaire.questions.length}
        showAssignUser={false}
      />
    </Box>
  );
};
